/**
 * AccountColumn - Renders the middle column of account nodes
 *
 * Handles both individual account nodes and lumped nodes,
 * with tier dividers between groups.
 */

import { memo, useCallback } from 'react';
import type {
  AccountNode as AccountNodeType,
  LumpedNode as LumpedNodeType,
  PositionedNode,
  FlowNode,
} from './types';
import { isAccountNode, isLumpedNode } from './types';
import { AccountNode, LumpedNode } from './nodes';
import { COLUMNS, NODE, COLORS } from './constants';

export interface AccountColumnProps {
  positionedNodes: PositionedNode[];
  onNodeClick?: (node: FlowNode) => void;
  /** Called when mouse enters a node */
  onNodeHover?: (node: FlowNode | null, event?: React.MouseEvent) => void;
  /** ID of the currently highlighted node (if any) */
  highlightedNodeId?: string | null;
  /** Currently expanded tiers */
  expandedTiers?: string[];
}

function AccountColumnComponent({
  positionedNodes,
  onNodeClick,
  onNodeHover,
  highlightedNodeId,
  expandedTiers = [],
}: AccountColumnProps) {
  // Filter to account and lumped nodes
  const accountNodes = positionedNodes.filter(
    (p): p is PositionedNode & { node: AccountNodeType } => isAccountNode(p.node)
  );
  const lumpedNodes = positionedNodes.filter(
    (p): p is PositionedNode & { node: LumpedNodeType } => isLumpedNode(p.node)
  );

  // Combine and sort by Y position
  const allNodes = [...accountNodes, ...lumpedNodes].sort((a, b) => a.y - b.y);

  const handleAccountMouseEnter = useCallback(
    (node: AccountNodeType, event: React.MouseEvent) => {
      onNodeHover?.(node, event);
    },
    [onNodeHover]
  );

  const handleLumpedMouseEnter = useCallback(
    (node: LumpedNodeType, event: React.MouseEvent) => {
      onNodeHover?.(node, event);
    },
    [onNodeHover]
  );

  const handleMouseLeave = useCallback(() => {
    onNodeHover?.(null);
  }, [onNodeHover]);

  if (allNodes.length === 0) {
    return null;
  }

  // Calculate tier divider positions
  // Don't show tier label when transitioning to a lumped node (it already has the tier name in its display)
  const tierDividers: { y: number; label: string; showLabel: boolean }[] = [];
  let prevTier: string | null = null;

  for (const positioned of allNodes) {
    let currentTier: string;
    if (isAccountNode(positioned.node)) {
      currentTier = positioned.node.tier;
    } else {
      currentTier = positioned.node.tier;
    }

    if (prevTier !== null && prevTier !== currentTier) {
      // Add divider between tiers
      // Don't show label text if the first node of this tier is a lumped node
      // (lumped nodes already display the tier name)
      const isLumped = isLumpedNode(positioned.node);
      tierDividers.push({
        y: positioned.y - 12,
        label:
          currentTier === 'B'
            ? 'Savings & Investments'
            : currentTier === 'D'
              ? 'Other Liabilities'
              : 'Other Assets',
        showLabel: !isLumped,
      });
    }

    prevTier = currentTier;
  }

  // Determine divider line positions
  const dividerStartX = COLUMNS.ACCOUNT_X - 60;
  const dividerEndX = COLUMNS.ACCOUNT_X + NODE.ACCOUNT_WIDTH + 60;

  return (
    <g className="account-column">
      {/* Tier dividers */}
      {tierDividers.map((divider, index) => (
        <g key={`divider-${index}`} className="tier-divider-group">
          {/* Divider line */}
          <line
            x1={dividerStartX}
            y1={divider.y}
            x2={dividerEndX}
            y2={divider.y}
            stroke={COLORS.background.divider}
            strokeWidth={1}
            strokeDasharray="4,4"
          />
          {/* Tier label - only show if not transitioning to a lumped node */}
          {divider.showLabel && (
            <text
              x={dividerStartX}
              y={divider.y + 15}
              className="tier-label"
              fill={COLORS.text.secondary}
              fontSize={10}
              fontWeight={500}
              style={{ textTransform: 'uppercase', letterSpacing: '0.3px' }}
            >
              {divider.label}
            </text>
          )}
        </g>
      ))}

      {/* Account nodes */}
      {accountNodes.map((positioned) => (
        <AccountNode
          key={positioned.node.id}
          node={positioned.node}
          x={positioned.x}
          y={positioned.y}
          width={positioned.width}
          height={positioned.height}
          onClick={onNodeClick as ((node: AccountNodeType) => void) | undefined}
          onMouseEnter={handleAccountMouseEnter}
          onMouseLeave={handleMouseLeave}
          isHighlighted={highlightedNodeId === positioned.node.id}
        />
      ))}

      {/* Lumped nodes */}
      {lumpedNodes.map((positioned) => (
        <LumpedNode
          key={positioned.node.id}
          node={positioned.node}
          x={positioned.x}
          y={positioned.y}
          width={positioned.width}
          height={positioned.height}
          onClick={onNodeClick as ((node: LumpedNodeType) => void) | undefined}
          onMouseEnter={handleLumpedMouseEnter}
          onMouseLeave={handleMouseLeave}
          isHighlighted={highlightedNodeId === positioned.node.id}
          isExpanded={expandedTiers.includes(positioned.node.tier)}
        />
      ))}
    </g>
  );
}

export const AccountColumn = memo(AccountColumnComponent);
export default AccountColumn;
